/**
 * Scheduled runs — per-account list + editor.
 *
 * The current account is JWT-derived server-side: this view never carries
 * a subdomain, never lets the user pick an account.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Calendar, Info, Loader2, PencilIcon, Play, Plus, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Switch } from '@/client/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  createScheduledRun,
  deleteScheduledRun,
  listScheduledRuns,
  patchScheduledRun,
  type Schedule,
  type ScheduledRunTask,
} from '@/client/lib/scheduledRunsApi';
import { useAuth } from '@/client/app/providers/auth-context';

const SCHEDULE_OPTIONS: { value: Schedule; label: string }[] = [
  { value: 'daily', label: 'Daily (06:00 UTC)' },
  { value: 'weekly', label: 'Weekly (Mon 07:00 UTC)' },
  { value: 'monthly', label: 'Monthly (1st of month, 08:00 UTC)' },
  { value: 'manual', label: 'Manual only' },
];

const NAME_RE = /^[a-z0-9-]{1,64}$/;
const QUERY_KEY = ['scheduled-runs'] as const;

function formatRelative(updated: string | null): string {
  if (!updated) return '';
  const ts = new Date(updated).getTime();
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export interface ScheduledRunsViewProps {
  /**
   * Fired by the per-row Test button. The host (App.tsx) is responsible for
   * switching to the chat view, opening a new thread, and appending the
   * prompt as the first user message — the agent then responds live in chat,
   * which is the same code path normal user messages take.
   */
  onTestRun?: (task: ScheduledRunTask) => void;
}

export function ScheduledRunsView({ onTestRun }: ScheduledRunsViewProps) {
  const [editing, setEditing] = useState<{ mode: 'new' } | { mode: 'edit'; task: ScheduledRunTask } | null>(null);

  const {
    data: tasks,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listScheduledRuns,
  });

  if (editing) {
    return (
      <ScheduledRunEditor
        task={editing.mode === 'edit' ? editing.task : null}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="text-sm font-medium">Scheduled runs</div>
          <div className="text-[11px] text-muted-foreground">
            Recurring Gaia tasks for this account. Daily, weekly, and monthly schedules fire on shared crons.
          </div>
        </div>
        <button
          onClick={() => setEditing({ mode: 'new' })}
          className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="size-3.5" />
          New task
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Loading...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            Failed to load tasks: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : tasks && tasks.length > 0 ? (
          <div className="flex flex-col gap-2">
            {tasks.map((task) => (
              <TaskRow
                key={task.name}
                task={task}
                onEdit={() => setEditing({ mode: 'edit', task })}
                onTest={onTestRun ? () => onTestRun(task) : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-xs text-muted-foreground">
            <Calendar className="size-8 opacity-40" />
            <div>No scheduled runs yet.</div>
            <div className="opacity-70">Click "New task" to schedule a recurring Gaia run for this account.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  onEdit,
  onTest,
}: {
  task: ScheduledRunTask;
  onEdit: () => void;
  onTest?: () => void;
}) {
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => patchScheduledRun(task.name, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update task'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteScheduledRun(task.name),
    onSuccess: () => {
      toast.success(`Deleted "${task.name}"`);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete task'),
  });

  const handleDelete = useCallback(() => {
    if (!confirm(`Delete "${task.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate();
  }, [deleteMutation, task.name]);

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border bg-background p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{task.name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
              {task.schedule}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{task.description}</div>
          {task.updated && (
            <div className="mt-1 text-[10px] text-muted-foreground/70">Last edited {formatRelative(task.updated)}</div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Switch
            checked={task.enabled}
            disabled={toggleMutation.isPending}
            onCheckedChange={(v) => toggleMutation.mutate(v)}
            className="scale-75"
          />
          {onTest && (
            <button
              onClick={onTest}
              className="flex size-7 cursor-pointer items-center justify-center rounded-md text-primary transition-colors hover:bg-primary/10"
              aria-label="Test now"
              title="Test now — runs the prompt in a new chat under your user, without waiting for the cron"
            >
              <Play className="size-3.5" />
            </button>
          )}
          <button
            onClick={onEdit}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md transition-colors hover:bg-muted"
            aria-label="Edit"
            title="Edit"
          >
            <PencilIcon className="size-3.5" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Delete"
            title="Delete"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduledRunEditor({
  task,
  onClose,
}: {
  task: ScheduledRunTask | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { userEmail } = useAuth();
  const isGorgiasUser = userEmail?.endsWith('@gorgias.com') ?? false;
  const isNew = task === null;

  const [name, setName] = useState(task?.name ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [prompt, setPrompt] = useState(task?.prompt ?? '');
  const [schedule, setSchedule] = useState<Schedule>(task?.schedule ?? 'daily');
  const [enabled, setEnabled] = useState(task?.enabled ?? true);

  const validation = useMemo(() => {
    if (isNew && !NAME_RE.test(name)) {
      return 'Name must be lowercase letters, numbers, and hyphens (1–64 chars).';
    }
    if (!description.trim()) return 'Description is required.';
    if (!prompt.trim()) return 'Prompt is required.';
    if (prompt.length > 10_000) return 'Prompt must be 10,000 characters or fewer.';
    return null;
  }, [isNew, name, description, prompt]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return createScheduledRun({ name, description, prompt, schedule, enabled });
      }
      return patchScheduledRun(task!.name, { description, prompt, schedule, enabled });
    },
    onSuccess: () => {
      toast.success(isNew ? 'Task created' : 'Task updated');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save task'),
  });

  const handleSave = useCallback(() => {
    if (validation) {
      toast.error(validation);
      return;
    }
    saveMutation.mutate();
  }, [validation, saveMutation]);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="text-sm font-medium">{isNew ? 'New scheduled run' : `Edit "${task!.name}"`}</div>
          <div className="text-[11px] text-muted-foreground">
            The prompt below is sent verbatim as a user message to Gaia at each firing.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || validation !== null}
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveMutation.isPending && <Loader2 className="size-3 animate-spin" />}
            Save
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        <Field label="Name" hint="Lowercase, numbers, hyphens. Cannot be changed after creation.">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isNew}
            placeholder="morning-tickets-snapshot"
            className="h-8 rounded-md border bg-background px-2.5 text-xs outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
          />
        </Field>

        <Field label="Description" hint="One-liner shown in this list and in CI logs.">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={280}
            placeholder="Daily snapshot of yesterday's ticket volume by topic."
            className="h-8 rounded-md border bg-background px-2.5 text-xs outline-none focus:ring-1 focus:ring-ring"
          />
        </Field>

        <Field label="Schedule">
          <Select value={schedule} onValueChange={(v) => setSchedule(v as Schedule)}>
            <SelectTrigger className="h-8 cursor-pointer text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field
          label="Enabled"
          hint="Disabled tasks stay saved but don't fire on cron. Manual cadence only fires on demand."
        >
          <Switch checked={enabled} onCheckedChange={setEnabled} size="small" />
        </Field>

        <Field
          label="Prompt"
          hint={`Sent verbatim as the user message to Gaia. ${prompt.length}/10000 chars.`}
        >
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={12}
            maxLength={10_000}
            placeholder={
              isGorgiasUser
                ? 'Look at yesterday\'s tickets and post the top three topics to channel C0123456789.\nGroup by handover reason. Highlight any anomalies vs the prior 7-day average.'
                : 'Look at yesterday\'s tickets and summarise the top three topics.\nGroup by handover reason. Highlight any anomalies vs the prior 7-day average.'
            }
            className="resize-y rounded-md border bg-background p-2 font-mono text-xs leading-relaxed outline-none focus:ring-1 focus:ring-ring"
          />
        </Field>

        {isGorgiasUser && (
          <div className="flex gap-2 rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-[11px] text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0 text-blue-500" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-foreground">Sending results to Slack</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  @gorgias.com only
                </span>
              </div>
              <div>
                To have Gaia post the run output to Slack, instruct it in the prompt and include the
                destination ID:
              </div>
              <ul className="ml-3 list-disc space-y-0.5">
                <li>
                  <span className="font-medium text-foreground">Channels</span> — provide the channel
                  ID (e.g. <code className="rounded bg-muted px-1">C0123456789</code>) and invite the{' '}
                  <span className="font-medium text-foreground">Gaia</span> app to the channel first
                  (<code className="rounded bg-muted px-1">/invite @Gaia</code>). Public channels only.
                </li>
                <li>
                  <span className="font-medium text-foreground">Direct messages</span> — provide the
                  user ID (e.g. <code className="rounded bg-muted px-1">U0123456789</code>). No invite
                  needed.
                </li>
              </ul>
            </div>
          </div>
        )}

        {validation && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {validation}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        {hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

